"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { 
  Menu, 
  X, 
  Moon, 
  Sun, 
  Settings, 
  LogOut, 
  LayoutDashboard,
  ChevronDown,
  AlertCircle
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { getAuthInstance } from "@/firebase";
import { onAuthStateChanged, Auth, User as FirebaseUser, signOut } from "firebase/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslations } from 'next-intl';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const t = useTranslations('layout.navbar');
  
  // Auth state
  const [auth, setAuth] = useState<Auth | null>(null);
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  
  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Initialize auth with proper ready state
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const initializeAuth = async () => {
      try {
        const authInstance = await getAuthInstance();
        setAuth(authInstance);
        
        unsubscribe = onAuthStateChanged(authInstance, (user) => {
          setCurrentUser(user);
          setAuthReady(true);
        });
      } catch (err) {
        console.error("Error initializing auth:", err);
        setAuthReady(true); // Set ready even on error
      }
    };
    
    initializeAuth();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    if (!auth) return;
    
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Get user initials for avatar
  const getUserInitials = (user: FirebaseUser) => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map((name: string) => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };
  
  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm shadow-sm">
      <div className="container max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link href="/" className="flex items-center gap-3">
              <div className="relative">
                {/* Use SVG logos from public folder with theme switching */}
                {mounted && (
                  <>
                    {/* Light theme logo */}
                    <Image
                      src="/logoBlack.svg"
                      alt={t('logo')}
                      width={120}
                      height={32}
                      className="h-8 w-auto block dark:hidden"
                      priority
                    />
                    {/* Dark theme logo */}
                    <Image
                      src="/logoWhite.svg"
                      alt={t('logo')}
                      width={120}
                      height={32}
                      className="h-8 w-auto hidden dark:block"
                      priority
                    />
                  </>
                )}
                {/* Fallback while mounting */}
                {!mounted && (
                  <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                )}
              </div>
            </Link>
          </div>
          
          {/* Navigation Links */}
          <nav className="hidden md:flex items-center gap-6">
            <Link href="/features" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('features')}
            </Link>
            <Link href="/pricing" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('pricing')}
            </Link>
            <Link href="/whyus" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('whyUs')}
            </Link>
            <Link href="/contact" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('contact')}
            </Link>
            <Link href="/about-cookies" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('aboutCookies')}
            </Link>
          </nav>
          
          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Theme Toggle */}
            {mounted && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="rounded-full w-9 h-9 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t('toggleTheme')}
              >
                {theme === 'light' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>
            )}
            
            {/* Auth Section - With Loading State */}
            {!authReady ? (
              // Loading state
              <div className="flex items-center gap-2 px-3 py-1">
                <div className="h-3 w-3 animate-pulse bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                <div className="h-3 w-16 animate-pulse bg-gray-300 dark:bg-gray-700 rounded-full"></div>
              </div>
            ) : currentUser ? (
              // Logged In State
              <div className="flex items-center">
                {/* Dashboard Link - Text on larger screens, icon on mobile */}
                <Button 
                  variant="ghost" 
                  asChild
                  className="mr-1 px-2 sm:px-3 h-9"
                >
                  <Link href="/dashboard" className="flex items-center">
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="hidden sm:inline ml-2">{t('dashboard')}</span>
                  </Link>
                </Button>
                
                {/* Profile Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-1 px-2 h-9"
                    >
                      {/* Avatar */}
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                        {getUserInitials(currentUser)}
                      </div>
                      <ChevronDown className="h-3 w-3 text-gray-500 hidden sm:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {currentUser.displayName || t('user')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {currentUser.email}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/dashboard" className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        {t('instrumentPanel')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/settings" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        {t('settings')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              // Not Logged In State
              <div className="hidden md:flex items-center gap-3">
                <Link 
                  href="/login" 
                  className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('login')}
                </Link>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm">
                  <Link href="/signup">{t('signup')}</Link>
                </Button>
              </div>
            )}
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
          <div className="container px-4 py-4 space-y-4">
            <Link href="/features" className="block py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              {t('features')}
            </Link>
            <Link href="/pricing" className="block py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              {t('pricing')}
            </Link>
            <Link href="/whyus" className="block py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              {t('whyUs')}
            </Link>
            <Link href="/contact" className="block py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              {t('contact')}
            </Link>
            <Link href="/about-cookies" className="block py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              {t('aboutCookies')}
            </Link>
            
            <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
              {!authReady ? (
                // Mobile - Loading state
                <div className="flex items-center gap-2 py-2">
                  <div className="h-4 w-4 animate-pulse bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                  <div className="h-4 w-24 animate-pulse bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                </div>
              ) : currentUser ? (
                // Mobile - Logged In
                <>
                  <Link href="/dashboard" className="flex items-center gap-2 py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                    <LayoutDashboard className="h-4 w-4" />
                    {t('instrumentPanel')}
                  </Link>
                  <Link href="/settings" className="flex items-center gap-2 py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                    <Settings className="h-4 w-4" />
                    {t('settings')}
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center gap-2 py-2 text-base font-medium text-red-600 dark:text-red-400 w-full text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('logout')}
                  </button>
                </>
              ) : (
                // Mobile - Not Logged In
                <>
                  <Link href="/login" className="block py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                    {t('login')}
                  </Link>
                  <Link href="/signup" className="block py-2 text-base font-medium text-blue-600 dark:text-blue-400">
                    {t('signup')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}